import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LOGIN_PATH, PRIVACY_POLICY_PATH, TERMS_AND_CONDITIONS_PATH } from '@constants/routes';
import AuthLayout from '@components/Layout/auth/AuthLayout';
import { IoEyeOffOutline, IoEyeOutline } from 'react-icons/io5';
import {
    PASSWORD_MIN_Length,
    PASSWORD_MAX_Length,
    PASSWORD_PATTERN,
    PASSWORD_TITLE,
    RESET_OTP_MAX_Length,
    RESET_OTP_PATTERN,
    RESET_OTP_TITLE,
} from '@constants/form';
import ErrorMessage from '@components/Error/ErrorMessage';
import LoadingSpinner from '@components/Spinner';
import { setPasswordChange } from '@store/reducers/auth/loginSlice';
import { userNewPassword } from '@utils/login';
import { setTermsAccepted } from '@store/reducers/auth/signupSlice';

import { getDataUsingUserId } from '@utils/sharedUtils';
import { USER_INFO_TABLE } from '@constants/dynamoDBTableName';
import { formattedDateTime, updateUser } from '@utils/utils';
import { DRIPSET_LOGO } from '@constants/images';
import { FiMail, FiArrowRight, FiLock, FiClock, FiShield, FiKey } from 'react-icons/fi';

const ResetUserPassword = () => {
    const [confirmationCode, setConfirmationCode] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfrimPassword] =
        useState<boolean>(false);
    const [isClickable, setIsClickable] = useState<boolean>(false);
    const [countdown, setCountdown] = useState<number>(30);
    const [error, setError] = useState<string | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const loginEmail = useAppSelector((state) => state.login.email);
    const newPassword = useAppSelector((state) => state.login.newPassword);
    const session = useAppSelector((state) => state.login.session);
    const timerRef = useRef<number | null>(null);
    const userTermsAccepted = useAppSelector((state) => state.signup.termsAccepted);
    const [termsAccepted, setTerms] = useState(userTermsAccepted);
    const loggedInUserID = useAppSelector((state) => state.login.loggedInUserID);

    const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
        const message = 'Are you sure you want to leave?';
        event.preventDefault();
        event.returnValue = message;
        return message;
    }, []);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        };
    }, []);

    useEffect(() => {
        window.addEventListener('beforeunload', handleBeforeUnload);
        if (loginEmail === '') {
            navigate(LOGIN_PATH);
        } else {
            startCountdown();
        }
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [loginEmail, navigate, handleBeforeUnload]);

    const getErrorMessage = (error: any): string => {
        switch (error.name) {
            case 'CodeMismatchException':
                return 'Invalid confirmation code.';
            case 'InvalidParameterException':
                return 'Invalid parameters provided.';
            case 'UserNotFoundException':
                return 'Invalid credentials.';
            case 'LimitExceededException':
                return 'Too many requests. Please try again later.';
            case 'NotAuthorizedException':
                return 'You are not authorized to perform this action.';
            case 'ExpiredCodeException':
                return 'Verification code expired.';
            default:
                return (
                    error.message ||
                    'An unknown error occurred during password reset'
                );
        }
    };

    const handleTermsAcceptedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTerms(event.target.checked);
        dispatch(setTermsAccepted(event.target.checked));
    };

    const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmationCode(event.target.value);
        setError(null);
    };

    const handlePasswordChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setPassword(event.target.value);
        setError(null);
    };

    const handleConfirmPasswordChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setConfirmPassword(event.target.value);
        if (event.target.value !== password) {
            setError(t('auth.error.password_doest_not_match'));
        } else {
            setError(null);
        }
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword((prevShowPassword) => !prevShowPassword);
    };

    const handleToggleConfirmPasswordVisibility = () => {
        setShowConfrimPassword(
            (prevShowConfirmPassword) => !prevShowConfirmPassword
        );
    };

    const startCountdown = () => {
        setIsClickable(false);
        setCountdown(30);

        timerRef.current = window.setInterval(() => {
            setCountdown((prevCountdown) => {
                if (prevCountdown === 1) {
                    clearInterval(timerRef.current!);
                    setIsClickable(true);
                    return 30;
                }
                return prevCountdown - 1;
            });
        }, 1000);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (
            !(
                (event.key >= '0' && event.key <= '9') ||
                event.key === 'Backspace' ||
                event.key === 'Delete' ||
                event.key === 'ArrowLeft' ||
                event.key === 'ArrowRight' ||
                (event.ctrlKey && event.key.toLowerCase() === 'v') ||
                (event.ctrlKey && event.key.toLowerCase() === 'a')
            )
        ) {
            event.preventDefault();
        }
    };

    const handleFormSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        try {
            setIsLoading(true);
            if (!newPassword) {
                await handleResetPassword();
            } else {
                await userNewPassword(loginEmail, password, session);
                dispatch(setPasswordChange(true));
                navigate(LOGIN_PATH);
            }
        } catch (error: any) {
            setError(getErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async () => {
        try {
            setIsLoading(true);
            if (
                password !== '' &&
                confirmPassword !== '' &&
                password !== confirmPassword
            ) {
                setError(t('auth.error.password_doest_not_match'));
                setIsLoading(false);
                return;
            }

            await confirmResetPassword({
                username: loginEmail,
                confirmationCode: confirmationCode,
                newPassword: password,
            });

            dispatch(setPasswordChange(true));
            navigate(LOGIN_PATH);
            setIsLoading(false);
        } catch (error: any) {
            setError(getErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendConfirmationCode = async () => {
        try {
            setIsLoading(true);
            if (isClickable) {
                startCountdown();
                setIsClickable(false);
                await resetPassword({ username: loginEmail });
                setIsLoading(false);
            }
        } catch (error: any) {
            setError(getErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackClick = () => {
        navigate(-1);
    };

    return (
        <AuthLayout
            onBackClick={handleBackClick}
            className="relative !min-h-0 h-screen max-h-screen bg-cover bg-center overflow-hidden flex flex-col justify-between"
            style={{
                background: "url('/public/images/reset_password_bg.png') no-repeat center center / 100% 100%",
            }}
            hideBrandHeader={true}
            hideEmsenseLogo={true}
            backButtonClassName="!text-[#0346A2] font-bold left-6 md:left-10 mt-6"
        >
            <div className="flex-grow flex flex-col items-center justify-between py-2 px-4 z-10 w-full relative h-[calc(100vh-60px)] min-h-0 overflow-y-auto">
                {/* Top Section wrapper for Logo and Card */}
                <div className="flex flex-col items-center w-full justify-center flex-grow min-h-0">
                    {/* DripSet Logo */}
                    <div className="flex justify-center mb-8 shrink-0">
                        <img
                            src={DRIPSET_LOGO}
                            alt="DripSet"
                            className="h-16 object-contain"
                        />
                    </div>

                    {/* Card */}
                    <div className="card w-full max-w-[580px] border-none rounded-3xl bg-white shadow-2xl p-6 sm:p-8 relative shrink min-h-0 overflow-visible">
                        {/* Top Circle Icon with Lock and Dot Grids */}
                        <div className="flex justify-center -mt-16 mb-4 relative items-center shrink-0">
                            {/* Left Dot Grid */}
                            <svg width="32" height="10" viewBox="0 0 40 12" className="absolute right-[calc(50%+56px)] top-1/2 -translate-y-1/2 opacity-30">
                                <circle cx="4" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="12" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="20" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="28" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="36" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="4" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="12" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="20" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="28" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="36" cy="9" r="1.5" fill="#0346A2" />
                            </svg>

                            {/* Lock Circle */}
                            <div className="w-20 h-20 rounded-full bg-gradient-to-b from-[#dbe8fd] to-[#f0f6ff] border-[5px] border-white shadow-[0_4px_15px_rgba(0,0,0,0.08)] flex items-center justify-center relative">
                                <svg viewBox="0 0 24 24" className="w-12 h-12 text-[#0346A2]">
                                    {/* Lock Shackle */}
                                    <path d="M7 10V7a5 5 0 0 1 10 0v3" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    
                                    {/* Lock Body */}
                                    <rect x="3" y="10" width="18" height="11" rx="2.5" ry="2.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    
                                    {/* Keyhole */}
                                    <circle cx="12" cy="14.5" r="1.5" fill="currentColor" />
                                    <path d="M12 16v2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                                    
                                    {/* White Droplet Mask */}
                                    <path d="M18.5 13.5c0 0 3 3.5 3 5.5a3 3 0 1 1-6 0c0-2 3-5.5 3-5.5z" fill="white" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                                    
                                    {/* Blue Droplet */}
                                    <path d="M18.5 13.5c0 0 3 3.5 3 5.5a3 3 0 1 1-6 0c0-2 3-5.5 3-5.5z" fill="currentColor" />
                                </svg>
                            </div>

                            {/* Right Dot Grid */}
                            <svg width="32" height="10" viewBox="0 0 40 12" className="absolute left-[calc(50%+56px)] top-1/2 -translate-y-1/2 opacity-30">
                                <circle cx="4" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="12" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="20" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="28" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="36" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="4" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="12" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="20" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="28" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="36" cy="9" r="1.5" fill="#0346A2" />
                            </svg>
                        </div>

                        <div className="card-body px-3 py-1">
                            {/* Title */}
                            <h3 className="text-2xl font-extrabold text-[#0B0B0B] text-center mt-1 mb-0.5">
                                Reset <span className="text-[#0346A2]">Password?</span>
                            </h3>

                            {/* Divider Line */}
                            <div className="w-10 h-1 bg-[#0346A2] mx-auto my-2 rounded-full" />

                            {/* Description */}
                            {!newPassword && (
                                <p className="text-[#7D7A7A] text-[13px] text-center leading-normal max-w-sm mx-auto mb-4">
                                    {t('auth.reset_password.description.text')} {t('auth.reset_password.description.text_continued')}
                                </p>
                            )}
                            {newPassword && (
                                <p className="text-[#7D7A7A] text-[13px] text-center leading-normal max-w-sm mx-auto mb-4">
                                    Please set a new password for your account to continue.
                                </p>
                            )}

                            {error && <ErrorMessage message={error} />}

                            <form onSubmit={handleFormSubmit}>
                                {!newPassword && (
                                    <div className="mb-3">
                                        <label
                                            htmlFor="otp"
                                            className="block text-xs font-semibold text-[#3D3D3D] mb-1.5"
                                        >
                                            {t('auth.reset_password.otp')}
                                        </label>
                                        <div className="relative flex items-center">
                                            <FiKey className="absolute left-4 text-lg text-[#0346A2]" />
                                            <input
                                                type="text"
                                                id="otp"
                                                className="w-full h-11 pl-11 pr-24 border border-[#D9D9D9] rounded-xl focus:border-[#0346A2] focus:ring-1 focus:ring-[#0346A2] outline-none transition text-[#161616] placeholder-[#D9D9D9] text-sm font-medium"
                                                name="otp"
                                                onChange={handleCodeChange}
                                                onKeyDown={handleKeyDown}
                                                placeholder={t(
                                                    'auth.reset_password.otp_placeholder'
                                                )}
                                                maxLength={RESET_OTP_MAX_Length}
                                                pattern={RESET_OTP_PATTERN}
                                                title={RESET_OTP_TITLE}
                                                required
                                                aria-describedby="otp"
                                            />
                                            <div className="absolute right-3 flex items-center">
                                                {isClickable ? (
                                                    <div
                                                        className="text-[#0346A2] text-xs font-bold cursor-pointer hover:underline"
                                                        onClick={
                                                            handleResendConfirmationCode
                                                        }
                                                    >
                                                        {t(
                                                            'auth.reset_password.resend'
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="text-[#0346A2] text-xs font-bold opacity-45">
                                                        {t(
                                                            'auth.reset_password.resend'
                                                        )}{' '}
                                                        ({countdown}s)
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label
                                        className="block text-xs font-semibold text-[#3D3D3D] mb-1.5"
                                        htmlFor="password"
                                    >
                                        {t('auth.reset_password.new_password')}
                                    </label>
                                    <div className="relative flex items-center">
                                        <FiLock className="absolute left-4 text-lg text-[#0346A2]" />
                                        <input
                                            type={
                                                showPassword ? 'text' : 'password'
                                            }
                                            id="password"
                                            className="w-full h-11 pl-11 pr-11 border border-[#D9D9D9] rounded-xl focus:border-[#0346A2] focus:ring-1 focus:ring-[#0346A2] outline-none transition text-[#161616] placeholder-[#D9D9D9] text-sm font-medium"
                                            name="password"
                                            value={password}
                                            onChange={handlePasswordChange}
                                            placeholder={t(
                                                'auth.login.password_placeholder'
                                            )}
                                            minLength={PASSWORD_MIN_Length}
                                            maxLength={PASSWORD_MAX_Length}
                                            pattern={PASSWORD_PATTERN}
                                            required
                                            title={PASSWORD_TITLE}
                                            aria-describedby="password"
                                            autoComplete="new-password"
                                        />
                                        <span
                                            className="absolute right-4 cursor-pointer"
                                            onClick={handleTogglePasswordVisibility}
                                        >
                                            {showPassword ? (
                                                <IoEyeOutline className="text-secondary text-base" />
                                            ) : (
                                                <IoEyeOffOutline className="text-secondary text-base" />
                                            )}
                                        </span>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label
                                        className="block text-xs font-semibold text-[#3D3D3D] mb-1.5"
                                        htmlFor="confirm_password"
                                    >
                                        {t('auth.reset_password.confirm_password')}
                                    </label>
                                    <div className="relative flex items-center">
                                        <FiLock className="absolute left-4 text-lg text-[#0346A2]" />
                                        <input
                                            type={
                                                showConfirmPassword
                                                    ? 'text'
                                                    : 'password'
                                            }
                                            id="confirm_password"
                                            className="w-full h-11 pl-11 pr-11 border border-[#D9D9D9] rounded-xl focus:border-[#0346A2] focus:ring-1 focus:ring-[#0346A2] outline-none transition text-[#161616] placeholder-[#D9D9D9] text-sm font-medium"
                                            name="confirm_password"
                                            value={confirmPassword}
                                            onChange={handleConfirmPasswordChange}
                                            placeholder={t(
                                                'auth.reset_password.confirm_password_placeholder'
                                            )}
                                            minLength={PASSWORD_MIN_Length}
                                            maxLength={PASSWORD_MAX_Length}
                                            pattern={PASSWORD_PATTERN}
                                            required
                                            title={PASSWORD_TITLE}
                                            aria-describedby="confirm_password"
                                            autoComplete="new-password"
                                        />
                                        <span
                                            className="absolute right-4 cursor-pointer"
                                            onClick={
                                                handleToggleConfirmPasswordVisibility
                                            }
                                        >
                                            {showConfirmPassword ? (
                                                <IoEyeOutline className="text-secondary text-base" />
                                            ) : (
                                                <IoEyeOffOutline className="text-secondary text-base" />
                                            )}
                                        </span>
                                    </div>
                                    { newPassword && (
                                        <div className="mt-2.5 flex items-start gap-2">
                                            <input
                                                type="checkbox"
                                                id="termsCheckbox"
                                                name="termsCheckbox"
                                                checked={termsAccepted}
                                                onChange={handleTermsAcceptedChange}
                                                className="mt-1"
                                                required
                                            />
                                            <label htmlFor="termsCheckbox" className="text-[11px] text-[#7D7A7A] leading-normal">
                                                {t('auth.terms_condition')}{' '}
                                                <a href={TERMS_AND_CONDITIONS_PATH} target="_blank" className="text-[#0346A2] underline font-semibold">
                                                    {t('auth.terms')}
                                                </a>{' '}
                                                {t('auth.and')}{' '}
                                                <a href={PRIVACY_POLICY_PATH} target="_blank" className="text-[#0346A2] underline font-semibold">
                                                    {t('auth.privacy')}
                                                </a>
                                            </label>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-5">
                                    {isLoading ? (
                                        <div className="relative flex items-center justify-center w-full h-11 bg-gradient-to-r from-[#0346A2] to-[#1e6ae6] text-white font-bold rounded-xl transition shadow-lg">
                                            <LoadingSpinner className="text-white" />
                                        </div>
                                    ) : (
                                        <button
                                            type="submit"
                                            className="relative flex items-center justify-center w-full h-11 bg-gradient-to-r from-[#0346A2] to-[#1e6ae6] hover:opacity-95 text-white font-bold rounded-xl transition shadow-lg text-sm"
                                        >
                                            <span>{t('auth.reset_password.submit')}</span>
                                            <div className="absolute right-4 w-6.5 h-6.5 rounded-full bg-white/20 flex items-center justify-center">
                                                <FiArrowRight className="text-white text-sm" />
                                            </div>
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Bottom Row Features Card */}
                <div className="w-full max-w-[900px] bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#EAF0F9] p-4 flex flex-row items-center justify-between gap-4 mx-auto mt-2 shrink-0 z-10 hidden sm:flex px-8">
                    {/* Feature 1 */}
                    <div className="flex items-center gap-3.5 w-full md:w-auto">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#2f80ed] to-[#0449a5] flex items-center justify-center text-white shrink-0 shadow-[0_4px_12px_rgba(47,128,237,0.25)]">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                <path d="m9 11 2 2 4-4" />
                            </svg>
                        </div>
                        <div>
                            <h4 className="text-[13px] font-bold text-[#0346A2] mb-0.5">Secure & Private</h4>
                            <p className="text-[11px] text-[#4E5D78] mb-0 font-medium leading-tight">Your information is always protected with us.</p>
                        </div>
                    </div>

                    <div className="w-[1px] h-9 bg-[#e0ebfd] shrink-0" />

                    {/* Feature 2 */}
                    <div className="flex items-center gap-3.5 w-full md:w-auto">
                        <div className="w-14 h-14 rounded-2xl bg-[#eaf2ff] flex items-center justify-center text-[#0346A2] shrink-0">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0346A2" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                                <path d="M2 9h5" />
                                <path d="M1 12h7" />
                                <path d="M2 15h5" />
                                <circle cx="16" cy="12" r="5" />
                                <path d="M16 9v3l2 1" />
                            </svg>
                        </div>
                        <div>
                            <h4 className="text-[13px] font-bold text-[#0346A2] mb-0.5">Quick & Easy</h4>
                            <p className="text-[11px] text-[#4E5D78] mb-0 font-medium leading-tight">Reset your password in just a few steps.</p>
                        </div>
                    </div>

                    <div className="w-[1px] h-9 bg-[#e0ebfd] shrink-0" />

                    {/* Feature 3 */}
                    <div className="flex items-center gap-3.5 w-full md:w-auto">
                        <div className="w-14 h-14 rounded-2xl bg-[#eaf2ff] flex items-center justify-center text-[#0346A2] shrink-0">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0346A2" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                                <rect x="3" y="5" width="18" height="14" rx="3" />
                                <path d="M3 7l9 6 9-6" />
                            </svg>
                        </div>
                        <div>
                            <h4 className="text-[13px] font-bold text-[#0346A2] mb-0.5">Email Verification</h4>
                            <p className="text-[11px] text-[#4E5D78] mb-0 font-medium leading-tight">We'll send a secure code to your email.</p>
                        </div>
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
};

export default ResetUserPassword;
